/**
 * Vehicle Service
 * Handles vehicle management operations for providers
 */

import { apiClient, ApiClient } from './api';

/**
 * Vehicle types
 */
export type VehicleType = 'rolloff_truck' | 'box_truck' | 'pickup';
export type VehicleStatus = 'available' | 'assigned' | 'maintenance' | 'out_of_service';

/**
 * Vehicle DTO
 */
export interface VehicleDTO {
  id: string;
  vehicleNumber: string;
  make: string;
  model: string;
  year: number;
  type: VehicleType;
  vin: string;
  licensePlate: string;
  status: VehicleStatus;
  odometer: number;
  driverAssignment?: {
    driverId: string;
    driverName: string;
    assignmentDate: string;
  };
  dotCompliance: {
    lastInspectionDate: string;
    expirationDate: string;
    isCompliant: boolean;
    isExpiringSoon: boolean;
  };
  maintenanceDue: boolean;
  maintenanceRecords?: MaintenanceRecordDTO[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Maintenance record DTO
 */
export interface MaintenanceRecordDTO {
  id: string;
  date: string;
  type: 'scheduled' | 'repair' | 'inspection';
  description: string;
  odometer: number;
  cost?: number;
  performedBy?: string;
}

/**
 * List vehicles response
 */
export interface VehiclesResponse {
  vehicles: VehicleDTO[];
  total: number;
  byStatus: {
    available: number;
    assigned: number;
    maintenance: number;
    out_of_service: number;
  };
}

/**
 * Create vehicle request
 */
export interface CreateVehicleRequest {
  vehicleNumber: string;
  vehicleType: VehicleType;
  make: string;
  model: string;
  year: number;
  vin: string;
  licensePlate: string;
  odometer: number;
  dotInspectionDate: string;
  dotExpirationDate: string;
  notes?: string;
}

/**
 * Assign driver request
 */
export interface AssignDriverRequest {
  driverId: string;
}

/**
 * Record maintenance request
 */
export interface RecordMaintenanceRequest {
  type: 'scheduled' | 'repair' | 'inspection';
  description: string;
  odometer: number;
  cost?: number;
  performedBy?: string;
}

/**
 * Update odometer request
 */
export interface UpdateOdometerRequest {
  newReading: number;
}

/**
 * Update DOT inspection request
 */
export interface UpdateDOTRequest {
  inspectionDate: string;
  expirationDate: string;
}

/**
 * VehicleService class
 */
export class VehicleService {
  private client: ApiClient;

  constructor(client: ApiClient = apiClient) {
    this.client = client;
  }

  /**
   * List all vehicles for the provider
   */
  async list(filters?: { status?: VehicleStatus; maintenanceDue?: boolean }): Promise<VehiclesResponse> {
    const params = new URLSearchParams();
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.maintenanceDue !== undefined) {
      params.append('maintenance_due', String(filters.maintenanceDue));
    }

    const queryString = params.toString();
    const endpoint = `/provider/resources/vehicles${queryString ? `?${queryString}` : ''}`;

    return this.client.get<VehiclesResponse>(endpoint);
  }

  /**
   * Get a single vehicle by ID
   */
  async getById(id: string): Promise<VehicleDTO> {
    return this.client.get<VehicleDTO>(`/provider/resources/vehicles/${id}`);
  }

  /**
   * Create a new vehicle
   */
  async create(data: CreateVehicleRequest): Promise<VehicleDTO> {
    return this.client.post<VehicleDTO>('/provider/resources/vehicles', data);
  }

  /**
   * Update vehicle details
   */
  async update(id: string, data: Partial<CreateVehicleRequest>): Promise<VehicleDTO> {
    return this.client.put<VehicleDTO>(`/provider/resources/vehicles/${id}`, data);
  }

  /**
   * Delete a vehicle
   */
  async delete(id: string): Promise<void> {
    return this.client.delete<void>(`/provider/resources/vehicles/${id}`);
  }

  /**
   * Assign vehicle to driver
   */
  async assignDriver(id: string, data: AssignDriverRequest): Promise<VehicleDTO> {
    return this.client.post<VehicleDTO>(`/provider/resources/vehicles/${id}/assign-driver`, data);
  }

  /**
   * Unassign driver from vehicle
   */
  async unassignDriver(id: string): Promise<VehicleDTO> {
    return this.client.post<VehicleDTO>(`/provider/resources/vehicles/${id}/unassign-driver`, {});
  }

  /**
   * Record maintenance for vehicle
   */
  async recordMaintenance(id: string, data: RecordMaintenanceRequest): Promise<VehicleDTO> {
    return this.client.post<VehicleDTO>(`/provider/resources/vehicles/${id}/maintenance`, data);
  }

  /**
   * Complete maintenance and return to service
   */
  async completeMaintenance(id: string): Promise<VehicleDTO> {
    return this.client.post<VehicleDTO>(`/provider/resources/vehicles/${id}/complete-maintenance`, {});
  }

  /**
   * Update odometer reading
   */
  async updateOdometer(id: string, data: UpdateOdometerRequest): Promise<VehicleDTO> {
    return this.client.put<VehicleDTO>(`/provider/resources/vehicles/${id}/odometer`, data);
  }

  /**
   * Update DOT inspection
   */
  async updateDOT(id: string, data: UpdateDOTRequest): Promise<VehicleDTO> {
    return this.client.put<VehicleDTO>(`/provider/resources/vehicles/${id}/dot-inspection`, data);
  }
}

// Export singleton instance
export const vehicleService = new VehicleService();
